/**
 * tallyDataSource.ts — WatermelonDB → TallyExportInput bridge.
 *
 * Loads a store's Sales invoices (+ credit notes) and Purchase orders for a
 * date range and maps them into the plain voucher objects `buildTallyXml`
 * consumes. All DB access lives here; the XML builder stays pure.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Invoice, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Customer } from '@/db/models/Customers';
import { PurchaseOrder, PoLineItem, Supplier } from '@/db/models/Inventory';
import Store from '@/db/models/Store';
import Merchant from '@/db/models/Merchant';
import type { TallyExportInput, TallySalesInput, TallyPurchaseInput } from './tallyExport';

const CHUNK = 500;

async function fetchInChunks<T>(ids: string[], run: (batch: string[]) => Promise<T[]>): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    out.push(...(await run(ids.slice(i, i + CHUNK))));
  }
  return out;
}

export interface TallyRangeResult extends TallyExportInput {
  salesCount: number;
  purchaseCount: number;
}

/**
 * Load Sales + Purchase vouchers for [from, to] (inclusive of the `to` day if
 * the caller passes an end-of-day timestamp).
 */
export async function loadTallyRange(
  storeId: string,
  from: Date,
  to: Date,
): Promise<TallyRangeResult> {
  const store = await database.get<Store>('stores').find(storeId);
  let merchant: Merchant | null = null;
  try {
    merchant = await database.get<Merchant>('merchants').find(store.merchantId);
  } catch {
    merchant = null;
  }
  const companyName = merchant?.businessName || store.name || 'Company';

  // ── Sales invoices (SALE + CREDIT_NOTE) ──
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', Q.oneOf(['SALE', 'CREDIT_NOTE'])),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lte(to.getTime())),
  ).fetch();

  const custIds = [...new Set(invoices.map((i) => i.customerId).filter(Boolean))] as string[];
  const customers = await fetchInChunks(custIds, (batch) =>
    database.get<Customer>('customers').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const custMap = new Map<string, Customer>();
  for (const c of customers) custMap.set(c.id, c);

  const sales: TallySalesInput[] = invoices.map((inv) => {
    const c = inv.customerId ? custMap.get(inv.customerId) : null;
    return {
      date: inv.createdAt,
      voucherNumber: inv.invoiceNumber,
      partyName: c?.name?.trim() || '',
      partyGstin: c?.gstin ?? null,
      taxable: inv.taxableAmount,
      cgst: inv.cgstAmount,
      sgst: inv.sgstAmount,
      igst: inv.igstAmount,
      cess: inv.cessAmount,
      roundOff: inv.roundOff,
      total: inv.totalAmount,
      isCreditNote: inv.type === 'CREDIT_NOTE',
      narration: c?.name ? `${inv.type === 'CREDIT_NOTE' ? 'Credit note' : 'Sale'} to ${c.name}` : undefined,
    };
  });

  // ── Purchases (received/partial POs with a supplier bill) ──
  const pos = await database.get<PurchaseOrder>('purchase_orders').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.oneOf(['RECEIVED', 'PARTIAL'])),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lte(to.getTime())),
  ).fetch();

  const poIds = pos.map((p) => p.id);
  const poLines = await fetchInChunks(poIds, (batch) =>
    database.get<PoLineItem>('po_line_items').query(
      Q.where('purchase_order_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const linesByPo = new Map<string, PoLineItem[]>();
  for (const li of poLines) {
    const arr = linesByPo.get(li.purchaseOrderId) ?? [];
    arr.push(li);
    linesByPo.set(li.purchaseOrderId, arr);
  }

  const supIds = [...new Set(pos.map((p) => p.supplierId).filter(Boolean))];
  const suppliers = await fetchInChunks(supIds, (batch) =>
    database.get<Supplier>('suppliers').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const supMap = new Map<string, Supplier>();
  for (const s of suppliers) supMap.set(s.id, s);

  const purchases: TallyPurchaseInput[] = pos.map((po) => {
    const sup = supMap.get(po.supplierId);
    const lines = linesByPo.get(po.id) ?? [];
    const cgst = lines.reduce((s, l) => s + l.cgstAmount, 0);
    const sgst = lines.reduce((s, l) => s + l.sgstAmount, 0);
    const igst = lines.reduce((s, l) => s + l.igstAmount, 0);
    const taxable = lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
    return {
      date: po.supplierInvoiceDate ?? po.createdAt,
      voucherNumber: po.supplierInvoiceNo?.trim() || po.poNumber,
      supplierName: sup?.name?.trim() || 'Sundry Creditors',
      supplierGstin: sup?.gstin ?? null,
      taxable,
      cgst,
      sgst,
      igst,
      total: po.totalAmount,
      narration: sup?.name ? `Purchase from ${sup.name} (${po.poNumber})` : `Purchase ${po.poNumber}`,
    };
  });

  return {
    companyName,
    sales,
    purchases,
    salesCount: sales.length,
    purchaseCount: purchases.length,
  };
}
